import { useAuth } from "@/auth/hooks/useAuth";
import {
  DASHBOARD_QUERY_GC_TIME,
  isDashboardPersistedQueryKey,
} from "@/dashboard/dashboardQueryConfig";
import {
  PROJECT_QUERY_GC_TIME,
  isProjectPersistedQueryKey,
} from "@/dashboard/projectQueryConfig";
import {
  PROGRAM_QUERY_GC_TIME,
  isProgramPersistedQueryKey,
} from "@/dashboard/programQueryConfig";
import { clearAllVolatileBrowserCaches } from "@/libs/browserStorage";
import {
  QueryClient,
  dehydrate,
  hydrate,
  useQueryClient,
  type DehydratedState,
  type QueryKey,
} from "@tanstack/react-query";
import { createStore, del, get, keys, set } from "idb-keyval";
import { useEffect } from "react";

const DASHBOARD_QUERY_CACHE_BUSTER = "dashboard-query-cache-network-authoritative-v2";
const DASHBOARD_QUERY_CACHE_KEY_PREFIX = "dashboard-query-cache";
const DASHBOARD_QUERY_CACHE_STORE = createStore(
  "verdeo-react-query-cache",
  "dashboard",
);
const DASHBOARD_QUERY_PERSIST_DEBOUNCE_MS = 300;

type PersistedDashboardQuerySnapshot = {
  buster: string;
  clientState: DehydratedState;
  timestamp: number;
};

export const queryClient = new QueryClient();

const PERSISTED_QUERY_GC_TIME = Math.max(
  DASHBOARD_QUERY_GC_TIME,
  PROJECT_QUERY_GC_TIME,
  PROGRAM_QUERY_GC_TIME,
);

const shouldPersistVerdeoQuery = (queryKey: QueryKey) =>
  !queryKey.includes("no-cache") &&
  (isDashboardPersistedQueryKey(queryKey) ||
    isProjectPersistedQueryKey(queryKey) ||
    isProgramPersistedQueryKey(queryKey));

let hydratedDashboardQueryScopeKey: string | null = null;

function readStoredUserId() {
  if (typeof window === "undefined") return null;

  const rawUser =
    localStorage.getItem("user") || sessionStorage.getItem("user");
  if (!rawUser) return null;

  try {
    const parsedUser = JSON.parse(rawUser);
    const userId = Number(parsedUser?.id || 0);
    return Number.isFinite(userId) && userId > 0 ? String(userId) : null;
  } catch {
    return null;
  }
}

function readApiBase() {
  return String(import.meta.env.VITE_API_ENDPOINT || "")
    .trim()
    .replace(/\/+$/, "");
}

function buildPersistedDashboardQueryStorageKey(scopeKey: string) {
  return `${DASHBOARD_QUERY_CACHE_KEY_PREFIX}::${scopeKey}`;
}

function isPersistedDashboardQueryStorageKey(key: IDBValidKey) {
  return (
    typeof key === "string" &&
    key.startsWith(`${DASHBOARD_QUERY_CACHE_KEY_PREFIX}::`)
  );
}

export function getPersistedDashboardQueryScopeKey() {
  const apiBase = readApiBase();
  const userId = readStoredUserId();

  if (!apiBase || !userId) {
    return null;
  }

  return `${apiBase}::user:${userId}`;
}

async function clearPersistedDashboardQueriesForApi(apiBase: string) {
  if (!apiBase) return;

  const storedKeys = await keys(DASHBOARD_QUERY_CACHE_STORE);
  await Promise.all(
    storedKeys
      .filter(
        (key) =>
          isPersistedDashboardQueryStorageKey(key) &&
          key.startsWith(`${DASHBOARD_QUERY_CACHE_KEY_PREFIX}::${apiBase}::`),
      )
      .map((key) => del(key, DASHBOARD_QUERY_CACHE_STORE)),
  );
}

export async function clearPersistedDashboardQueryCache() {
  const storedKeys = await keys(DASHBOARD_QUERY_CACHE_STORE);
  await Promise.all(
    storedKeys
      .filter(isPersistedDashboardQueryStorageKey)
      .map((key) => del(key, DASHBOARD_QUERY_CACHE_STORE)),
  );
  hydratedDashboardQueryScopeKey = null;
}

export async function refreshCachedDataAfterMutation() {
  clearAllVolatileBrowserCaches();
  await clearPersistedDashboardQueryCache();

  queryClient.removeQueries({
    type: "inactive",
  });

  await queryClient.invalidateQueries({
    refetchType: "active",
  });
}

async function persistDashboardQueriesToStorage(client: QueryClient) {
  const apiBase = readApiBase();
  if (!apiBase) return;

  const scopeKey = getPersistedDashboardQueryScopeKey();
  if (!scopeKey) {
    hydratedDashboardQueryScopeKey = null;
    await clearPersistedDashboardQueriesForApi(apiBase);
    return;
  }

  const clientState = dehydrate(client, {
    shouldDehydrateQuery: (query) =>
      shouldPersistVerdeoQuery(query.queryKey) &&
      typeof query.state.data !== "undefined" &&
      query.state.status === "success" &&
      !query.state.isInvalidated,
  });

  const storageKey = buildPersistedDashboardQueryStorageKey(scopeKey);
  hydratedDashboardQueryScopeKey = scopeKey;

  if (clientState.queries.length === 0) {
    await del(storageKey, DASHBOARD_QUERY_CACHE_STORE);
    return;
  }

  await set(
    storageKey,
    {
      buster: DASHBOARD_QUERY_CACHE_BUSTER,
      clientState,
      timestamp: Date.now(),
    } satisfies PersistedDashboardQuerySnapshot,
    DASHBOARD_QUERY_CACHE_STORE,
  );
}

export async function hydratePersistedDashboardQueries(client: QueryClient) {
  const scopeKey = getPersistedDashboardQueryScopeKey();
  if (!scopeKey) {
    hydratedDashboardQueryScopeKey = null;
    return;
  }

  const storageKey = buildPersistedDashboardQueryStorageKey(scopeKey);
  const snapshot = (await get(
    storageKey,
    DASHBOARD_QUERY_CACHE_STORE,
  )) as PersistedDashboardQuerySnapshot | undefined;

  hydratedDashboardQueryScopeKey = scopeKey;

  if (!snapshot) {
    return;
  }

  const hasExpired =
    Date.now() - snapshot.timestamp > PERSISTED_QUERY_GC_TIME;
  const hasValidShape =
    snapshot.buster === DASHBOARD_QUERY_CACHE_BUSTER &&
    typeof snapshot.timestamp === "number" &&
    snapshot.clientState &&
    typeof snapshot.clientState === "object";

  if (!hasValidShape || hasExpired) {
    await del(storageKey, DASHBOARD_QUERY_CACHE_STORE);
    return;
  }

  hydrate(client, snapshot.clientState);

  // Les données persistées accélèrent l'affichage, mais seule l'API fait foi.
  // L'invalidation garantit une revalidation réseau dès que les vues se montent.
  await client.invalidateQueries({
    predicate: (query) => shouldPersistVerdeoQuery(query.queryKey),
    refetchType: "none",
  });
}

export async function bootstrapQueryClient() {
  try {
    await hydratePersistedDashboardQueries(queryClient);
  } catch (error) {
    console.error("Failed to hydrate persisted dashboard queries", error);
  }
}

function startPersistedDashboardQuerySync(client: QueryClient) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  let debounceTimer: number | null = null;

  const flushPersistedDashboardQueries = () => {
    if (debounceTimer !== null) {
      window.clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    void persistDashboardQueriesToStorage(client);
  };

  const schedulePersistedDashboardQueries = () => {
    if (debounceTimer !== null) {
      window.clearTimeout(debounceTimer);
    }

    debounceTimer = window.setTimeout(() => {
      debounceTimer = null;
      void persistDashboardQueriesToStorage(client);
    }, DASHBOARD_QUERY_PERSIST_DEBOUNCE_MS);
  };

  const unsubscribe = client.getQueryCache().subscribe(
    schedulePersistedDashboardQueries,
  );

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      flushPersistedDashboardQueries();
    }
  };

  window.addEventListener("pagehide", flushPersistedDashboardQueries);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    unsubscribe();
    if (debounceTimer !== null) {
      window.clearTimeout(debounceTimer);
    }
    window.removeEventListener("pagehide", flushPersistedDashboardQueries);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}

export function PersistedDashboardQueryCacheSync() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => startPersistedDashboardQuerySync(queryClient), [queryClient]);

  useEffect(() => {
    const scopeKey = getPersistedDashboardQueryScopeKey();
    if (!scopeKey || hydratedDashboardQueryScopeKey === scopeKey) {
      return;
    }

    void hydratePersistedDashboardQueries(queryClient);
  }, [queryClient, user?.id]);

  return null;
}
